// --- src/data/m2/Unit1.js ---

export const M2_UNIT_1 = {
  id: "m2-u1",
  title: "Unit 1: Questions & Routines",
  topic: "Present Simple Interrogative",
  iconType: "map",
  grammarImage: "/m2-u1-grammar.jpg",
  grammarText: "Unit focus: questions with TO BE (Am/Is/Are?) + questions with DO/DOES + short answers (Yes, I am / No, I don't) + WH-questions (What/Where/When/Who/How).",
  
  sets: [
    // ═══════════════════════════════════════════════════════════════════════
    // SET 1: QUESTIONS WITH 'TO BE' (Unit 17.1-17.3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "u17-set1",
      title: "Set 1: Meeting New People",
      context: `*Lunes por la mañana - Cafetería universitaria*

Tom conoce a una nueva estudiante en la universidad.
"Hi! Are you a student here?" pregunta Tom.
"Yes, I am! I'm from Canada," responde ella.

*Monday morning - University cafeteria*

Tom meets a new student at the university.
"Hi! Are you a student here?" asks Tom.
"Yes, I am! I'm from Canada," she responds.`,
      
      questions: [
        { 
          type: "mc", 
          q: "Tom asks the new student: '_____ you a student?'", 
          options: ["Are", "Is", "Am"], 
          a: "Are",
          tip: "With 'You', always use 'Are'.",
          successTip: "ARE YOU...? Perfect question form! UK/US same! ✅"
        },
        { 
          type: "mc", 
          q: "Tom wants to know about the teacher: '_____ she a doctor?'", 
          options: ["Is", "Are", "Am"], 
          a: "Is",
          tip: "For 'She', 'He' or 'It', use 'Is'.",
          successTip: "IS SHE...? Third person question! He/She/It → IS! ✅"
        },
        { 
          type: "input", 
          q: "Tom sees two people: '_____ they engineers?' (to be)", 
          a: "Are",
          tip: "With 'They', always 'Are'.",
          successTip: "ARE THEY...? Plural question! We/They → ARE! 👥"
        },
        { 
          type: "mc", 
          q: "Tom describes someone: 'He is _____ teacher.'", 
          options: ["a", "an", "the"], 
          a: "a",
          tip: "Use 'a' before professions starting with consonant."
        },
        { 
          type: "input", 
          q: "Tom asks: 'Are you _____ Canada?' (de dónde eres)", 
          a: "from",
          tip: "'From' = where you're from.",
          successTip: "FROM! 'Where are you from?' = common question! 🌍"
        },
        { 
          type: "order", 
          q: "Traduce: '¿Es Ruby una actriz?'", 
          words: ["Is", "Ruby", "an", "actor?"], 
          a: "Is Ruby an actor?",
          tip: "Questions with 'to be': verb + subject + complement.",
          successTip: "PERFECT ORDER! Verb + Subject + Complement! 🎯"
        },
        { 
          type: "mc", 
          q: "Tom meets doctors: 'Are they doctors?' This is a simple question.", 
          options: ["True", "False"], 
          a: "True",
          successTip: "YES/NO QUESTION! Simple and direct! ✅"
        },
        { 
          type: "mc", 
          q: "Tom asks about nationality: '_____ he French?'", 
          options: ["Is", "Are", "Am"], 
          a: "Is",
          tip: "Nationalities use 'to be'.",
          successTip: "NATIONALITY! Use TO BE for countries/nationalities! 🇫🇷"
        },
        { 
          type: "input", 
          q: "Tom points at a computer: 'Is this _____ laptop?' (tu)", 
          a: "your",
          tip: "Possessive adjective."
        },
        { 
          type: "order", 
          q: "Traduce: '¿Eres enfermera?'", 
          words: ["Are", "you", "a", "nurse?"], 
          a: "Are you a nurse?",
          successTip: "PERFECT! Are you + profession? Next: action questions! 👩‍⚕️"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SET 2: QUESTIONS WITH 'DO/DOES' (Unit 17.8-17.10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "u17-set2",
      title: "Set 2: Daily Activities",
      context: `*Martes por la tarde - Oficina*

Sarah pregunta a su compañero sobre su trabajo:
"Do you work in an office?" pregunta.
"Yes, I do! And I start at 9am," responde él.

*Tuesday afternoon - Office*

Sarah asks her colleague about his work:
"Do you work in an office?" she asks.
"Yes, I do! And I start at 9am," he responds.`,
      
      questions: [
        { 
          type: "mc", 
          q: "Sarah asks her friend: '_____ you work in an office?'", 
          options: ["Do", "Does", "Is"], 
          a: "Do",
          tip: "For 'I, You, We, They', use auxiliary 'Do'.",
          successTip: "DO YOU...? I/You/We/They → DO! ✅"
        },
        { 
          type: "mc", 
          q: "Sarah asks about her boss: '_____ she live in Paris?'", 
          options: ["Does", "Do", "Are"], 
          a: "Does",
          tip: "With 'She' (ella), the auxiliary is 'Does'.",
          successTip: "DOES SHE...? He/She/It → DOES! Third person! ✅"
        },
        { 
          type: "input", 
          q: "Sarah asks about friends: 'Do they _____ swimming?' (ir)", 
          a: "go",
          tip: "After 'Do', verb returns to base form (without 'S').",
          successTip: "GO! After DO/DOES, verb has NO 'S'! Base form! 🏊"
        },
        { 
          type: "mc", 
          q: "Sarah asks about her brother: 'Does he _____ up at 6am?'", 
          options: ["get", "gets", "got"], 
          a: "get",
          tip: "The auxiliary 'Does' already has the 'S', so verb is normal: 'get'.",
          successTip: "GET! DOES already has the 'S', so verb stays base! 🛏️"
        },
        { 
          type: "input", 
          q: "Sarah asks you: '_____ you finish work at 4pm?' (auxiliar)", 
          a: "Do",
          tip: "Question with 'you' → Do."
        },
        { 
          type: "order", 
          q: "Traduce: '¿Trabaja Tom aquí?'", 
          words: ["Does", "Tom", "work", "here?"], 
          a: "Does Tom work here?",
          tip: "Questions: Do/Does + subject + base verb.",
          successTip: "PERFECT! Does + name + verb! Tom = he! 🎯"
        },
        { 
          type: "mc", 
          q: "Sarah checks: 'Do we eat lunch?' This uses 'do'.", 
          options: ["True", "False"], 
          a: "True",
          successTip: "WE → DO! Plural subject! 🍽️"
        },
        { 
          type: "mc", 
          q: "Sarah asks about an event: 'Does the party _____ at 7pm?'", 
          options: ["start", "starts", "starting"], 
          a: "start",
          tip: "After 'Does', always base form.",
          successTip: "START! Does + base verb! The party = it! 🎉"
        },
        { 
          type: "input", 
          q: "Sarah asks about a couple: '_____ they play tennis?' (auxiliar)", 
          a: "Do",
          tip: "They → Do."
        },
        { 
          type: "order", 
          q: "Traduce: '¿Te levantas temprano?'", 
          words: ["Do", "you", "get", "up", "early?"], 
          a: "Do you get up early?",
          successTip: "PERFECT! Do you + phrasal verb! Get up = wake up! 🌅"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SET 3: JOBS & PROFESSIONS (Unit 17 Vocabulary)
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "u17-set3",
      title: "Set 3: Jobs Around Town",
      context: `*Miércoles - Centro de la ciudad*

Emma visita diferentes lugares y conoce personas con distintos trabajos:
En el hospital: "She is a nurse."
En la escuela: "He is a teacher."
En el restaurante: "They are waiters."

*Wednesday - City center*

Emma visits different places and meets people with different jobs:
At the hospital: "She is a nurse."
At the school: "He is a teacher."
At the restaurant: "They are waiters."`,
      
      questions: [
        { 
          type: "mc", 
          q: "Emma meets someone at the hospital: 'She is a _____.'", 
          options: ["nurse", "nurses", "nursing"], 
          a: "nurse",
          tip: "After 'a', profession in singular.",
          successTip: "A NURSE! Article 'a' + singular profession! 👩‍⚕️"
        },
        { 
          type: "mc", 
          q: "Emma asks someone: 'Are you an _____?'", 
          options: ["actor", "engineer", "doctor"], 
          a: "actor",
          tip: "Use 'an' before vowel (Actor starts with 'A').",
          successTip: "AN ACTOR! AN before vowels (a,e,i,o,u)! 🎭"
        },
        { 
          type: "input", 
          q: "Emma sees someone working in a garden: 'He is a _____.' (jardinero)", 
          a: "gardener",
          tip: "Person who works in gardens."
        },
        { 
          type: "mc", 
          q: "Emma talks about a group: 'They are _____.'", 
          options: ["engineers", "engineer", "engineering"], 
          a: "engineers",
          tip: "With 'They', plural: engineers.",
          successTip: "ENGINEERS! They = plural! Add 'S'! 👷‍♂️👷‍♀️"
        },
        { 
          type: "input", 
          q: "Emma asks about Brad: 'Is Brad a _____?' (enfermero)", 
          a: "nurse",
          tip: "Male nurse = nurse (same word)."
        },
        { 
          type: "order", 
          q: "Traduce: 'Ella es recepcionista.'", 
          words: ["She", "is", "a", "receptionist."], 
          a: "She is a receptionist.",
          successTip: "PERFECT! She is A + profession! 💼"
        },
        { 
          type: "mc", 
          q: "Emma reads: 'Ruby and Farid are actors.' This statement is about their profession.", 
          options: ["True", "False"], 
          a: "True",
          successTip: "ACTORS! Plural profession! 🎬"
        },
        { 
          type: "mc", 
          q: "Emma sees someone with animals: 'He works with animals. He is a _____?'", 
          options: ["vet", "doctor", "teacher"], 
          a: "vet",
          tip: "Vet = veterinarian (animal doctor).",
          successTip: "VET! UK: 'vet', US: 'veterinarian'! Short form! 🐕"
        },
        { 
          type: "input", 
          q: "Emma asks in a restaurant: 'Is she a _____?' (mesera)", 
          a: "waitress",
          alts: ["waiter"],
          tip: "Person who serves food.",
          successTip: "WAITRESS! UK: 'waitress' common, US: trend toward 'server'! 🍽️"
        },
        { 
          type: "order", 
          q: "Traduce: 'Ellos son profesores.'", 
          words: ["They", "are", "teachers."], 
          a: "They are teachers.",
          successTip: "PERFECT! They are + plural profession! Next: speaking! 👨‍🏫👩‍🏫"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SET 4: WH-QUESTIONS (Unit 19.1-19.4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "u19-set4",
      title: "Set 4: Asking for Information",
      context: `*Jueves - Emma conoce a nuevas personas*

Emma practica hacer preguntas:
"What is your name?" - "Where are you from?"
"When is your birthday?" - "How old are you?"

*Thursday - Emma meets new people*

Emma practices asking questions:
"What is your name?" - "Where are you from?"
"When is your birthday?" - "How old are you?"`,
      
      questions: [
        { 
          type: "mc", 
          q: "Emma asks for identification: '_____ is your name?'", 
          options: ["What", "Where", "When"], 
          a: "What",
          tip: "What = what/which.",
          successTip: "WHAT! Asks for things/information! What is...? 💭"
        },
        { 
          type: "mc", 
          q: "Emma looks for a place: '_____ is the bank?'", 
          options: ["Where", "What", "Who"], 
          a: "Where",
          tip: "Where = where (location).",
          successTip: "WHERE! Asks for places/locations! Where is...? 📍"
        },
        { 
          type: "input", 
          q: "Emma asks about timing: '_____ is your birthday?' (cuándo)", 
          a: "When",
          tip: "When = when (time).",
          successTip: "WHEN! Asks for time/dates! When is...? 📅"
        },
        { 
          type: "mc", 
          q: "Emma asks about age: '_____ old are you?'", 
          options: ["How", "What", "Where"], 
          a: "How",
          tip: "How old = what age.",
          successTip: "HOW OLD! How + adjective = special questions! How old/tall/fast! 🎂"
        },
        { 
          type: "input", 
          q: "Emma hears a knock: '_____ is there?' (quién)", 
          a: "Who",
          tip: "Who = who (person).",
          successTip: "WHO! Asks for people! Who is...? 👤"
        },
        { 
          type: "order", 
          q: "Traduce: '¿Dónde está el café?'", 
          words: ["Where", "is", "the", "café?"], 
          a: "Where is the café?",
          successTip: "PERFECT! Where + to be + place! ☕"
        },
        { 
          type: "mc", 
          q: "Emma asks about meal time: '_____ do you eat lunch?'", 
          options: ["When", "What", "Who"], 
          a: "When",
          tip: "When = time question."
        },
        { 
          type: "input", 
          q: "Emma asks about location: '_____ does she work?' (dónde)", 
          a: "Where",
          tip: "Where + do/does = location of action.",
          successTip: "WHERE DOES...! Where + does + verb! Action location! 🏢"
        },
        { 
          type: "mc", 
          q: "Emma checks the clock: 'What is the time?' This asks for _____.", 
          options: ["the hour", "a place", "a person"], 
          a: "the hour",
          successTip: "THE HOUR! 'What time is it?' = common form! 🕐"
        },
        { 
          type: "order", 
          q: "Traduce: '¿Cuál es tu trabajo?'", 
          words: ["What", "is", "your", "job?"], 
          a: "What is your job?",
          successTip: "PERFECT! What is your...? Information question! Next: answers! 💼"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SET 5: SHORT ANSWERS (Unit 18.1-18.2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      id: "u18-set5",
      title: "Set 5: Yes or No?",
      context: `*Viernes - Conversaciones rápidas*

Las personas hacen preguntas y responden brevemente:
"Are you a doctor?" - "Yes, I am."
"Do you work here?" - "Yes, I do."
"Is she a nurse?" - "No, she isn't."

*Friday - Quick conversations*

People ask questions and answer briefly:
"Are you a doctor?" - "Yes, I am."
"Do you work here?" - "Yes, I do."
"Is she a nurse?" - "No, she isn't."`,
      
      questions: [
        { 
          type: "mc", 
          q: "Question: 'Are you a doctor?' Answer: 'Yes, _____.'", 
          options: ["I am", "you are", "I do"], 
          a: "I am",
          tip: "With 'to be', answer with 'to be': Yes, I am.",
          successTip: "YES, I AM! Match question type: ARE → AM/IS/ARE! ✅"
        },
        { 
          type: "mc", 
          q: "Question: 'Do you work here?' Answer: 'Yes, _____.'", 
          options: ["I do", "I am", "you do"], 
          a: "I do",
          tip: "With 'Do', answer with 'Do': Yes, I do.",
          successTip: "YES, I DO! Match question: DO/DOES → DO/DOES! ✅"
        },
        { 
          type: "input", 
          q: "Question: 'Is she a nurse?' Answer: 'No, she _____.' (negativo corto)", 
          a: "isn't",
          alts: ["is not"],
          tip: "Question with 'Is', answer with 'isn't'.",
          successTip: "ISN'T! Short negative: is not → isn't! 🚫"
        },
        { 
          type: "mc", 
          q: "Question: 'Does he live here?' Answer: 'No, _____.'", 
          options: ["he doesn't", "he isn't", "he don't"], 
          a: "he doesn't",
          tip: "Question with 'Does', answer with 'doesn't'.",
          successTip: "DOESN'T! Does not → doesn't! He/She/It! 🚫"
        },
        { 
          type: "input", 
          q: "Question: 'Are they doctors?' Answer: 'Yes, _____.' (they + verbo)", 
          a: "they are",
          alts: ["they're"],
          tip: "Plural subject."
        },
        { 
          type: "mc", 
          q: "Question: 'Do you speak English?' Answer: 'Yes, _____.'", 
          options: ["I do", "I am", "I speak"], 
          a: "I do",
          successTip: "I DO! Don't repeat main verb in short answer! ✅"
        },
        { 
          type: "input", 
          q: "Question: 'Is it your cat?' Answer: 'No, it _____.' (negativo)", 
          a: "isn't",
          alts: ["is not"],
          tip: "It + to be negative."
        },
        { 
          type: "mc", 
          q: "Question: 'Does she play tennis?' Answer: 'Yes, _____.'", 
          options: ["she does", "she is", "she plays"], 
          a: "she does",
          tip: "Don't repeat the main verb in short answer.",
          successTip: "SHE DOES! Keep it short! Don't say 'she plays'! 🎾"
        },
        { 
          type: "order", 
          q: "Traduce: 'Sí, yo sí.' (respuesta a Do you...?)", 
          words: ["Yes,", "I", "do."], 
          a: "Yes, I do.",
          successTip: "SHORT ANSWER! Subject + auxiliary only! ✅"
        },
        { 
          type: "mc", 
          q: "Question: 'Are you tired?' Answer: 'No, _____.'", 
          options: ["I'm not", "I don't", "I amn't"], 
          a: "I'm not",
          tip: "'Amn't' doesn't exist!",
          successTip: "I'M NOT! No contraction for 'am not' → use 'I'm not'! 😴"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SET 6-10: Continuar con el mismo estilo...
    // Por brevedad, incluyo solo algunos sets más representativos
    // ═══════════════════════════════════════════════════════════════════════

    {
      id: "u17-set6",
      title: "Set 6: Daily Routine Questions",
      context: `*Sábado - Hablando de rutinas*

Las personas hablan sobre sus días:
"I get up at 7am." - "She eats breakfast at 8am."
"Do you go to work by bus?" - "He watches TV in the evening."

*Saturday - Talking about routines*

People talk about their days:
"I get up at 7am." - "She eats breakfast at 8am."
"Do you go to work by bus?" - "He watches TV in the evening."`,
      
      questions: [
        { 
          type: "mc", 
          q: "What time do you wake up? 'I _____ up at 7am every day.'", 
          options: ["get", "gets", "got"], 
          a: "get",
          tip: "'Get up' = wake up.",
          successTip: "GET UP! Phrasal verb = wake up! UK/US same! 🛏️"
        },
        { 
          type: "mc", 
          q: "What does she do in the morning? 'She _____ breakfast at 8am.'", 
          options: ["eats", "eat", "eating"], 
          a: "eats",
          tip: "With 'She', add 'S': eats.",
          successTip: "EATS! She/He/It + verb+S! Third person! 🍳"
        },
        { 
          type: "input", 
          q: "'Do you _____ to work by bus?' (ir)", 
          a: "go",
          tip: "After 'Do', base form."
        },
        { 
          type: "mc", 
          q: "What does he do at night? 'He _____ TV in the evening.'", 
          options: ["watches", "watch", "watching"], 
          a: "watches",
          tip: "Verbs ending in -ch/-sh: add 'ES'.",
          successTip: "WATCHES! Watch → watches! -ch/-sh/-s/-x/-z + ES! 📺"
        },
        { 
          type: "input", 
          q: "'They _____ dinner at 7pm.' (tener/comer)", 
          a: "have",
          alts: ["eat"],
          tip: "Have dinner = eat dinner."
        },
        { 
          type: "mc", 
          q: "'Does she _____ swimming on Fridays?'", 
          options: ["go", "goes", "going"], 
          a: "go",
          tip: "After 'Does', base form.",
          successTip: "GO! Does + base verb! 'Go swimming' = common phrase! 🏊"
        },
        { 
          type: "order", 
          q: "Traduce: 'Como almuerzo a la 1pm.'", 
          words: ["I", "eat", "lunch", "at", "1pm."], 
          a: "I eat lunch at 1pm.",
          successTip: "PERFECT! Time expressions with 'at'! 🕐"
        },
        { 
          type: "mc", 
          q: "'We _____ work at 5pm.'", 
          options: ["finish", "finishes", "finishing"], 
          a: "finish",
          tip: "We/They → no 'S'."
        },
        { 
          type: "input", 
          q: "'He _____ to bed at 11pm.' (ir)", 
          a: "goes",
          tip: "'Go to bed' = go to sleep.",
          successTip: "GOES TO BED! UK: 'go to bed', US same! Common phrase! 😴"
        },
        { 
          type: "mc", 
          q: "'She reads every night.' This means she reads regularly.", 
          options: ["True", "False"], 
          a: "True",
          successTip: "TRUE! Present simple = habits/routines! Every night = regular! 📚"
        }
      ]
    },

    {
      id: "u17-set7",
      title: "Set 7: Error Detective",
      context: `*Domingo - Clase de inglés*

El profesor corrige errores comunes:
❌ "You is a doctor." → ✅ "Are you a doctor?"
❌ "Do he play tennis?" → ✅ "Does he play tennis?"

*Sunday - English class*

The teacher corrects common errors:
❌ "You is a doctor." → ✅ "Are you a doctor?"
❌ "Do he play tennis?" → ✅ "Does he play tennis?"`,
      
      questions: [
        { 
          type: "mc", 
          q: "Find the error: 'You is a doctor.' Correct form:", 
          options: ["Are you a doctor?", "Is you a doctor?"], 
          a: "Are you a doctor?",
          tip: "'You' never goes with 'is'. Use 'Are'.",
          successTip: "ARE YOU! Common mistake! You → ARE (never IS)! ✅"
        },
        { 
          type: "mc", 
          q: "Find the error: 'She work in a school.' Question form:", 
          options: ["Does she work", "Do she work"], 
          a: "Does she work",
          tip: "For questions with 'She', need auxiliary 'Does'.",
          successTip: "DOES SHE! She/He/It → DOES! ✅"
        },
        { 
          type: "mc", 
          q: "Fix the error: 'Do he play tennis?'", 
          options: ["Does he play", "No change"], 
          a: "Does he play",
          tip: "With 'He', use 'Does', not 'Do'.",
          successTip: "DOES HE! He/She/It → DOES (not DO)! ✅"
        },
        { 
          type: "mc", 
          q: "Fix the error: 'Are they works here?'", 
          options: ["Do they work", "Are they work"], 
          a: "Do they work",
          tip: "For actions, use 'Do/Does', not 'Are'.",
          successTip: "DO THEY WORK! Actions → DO/DOES! States → AM/IS/ARE! ✅"
        },
        { 
          type: "input", 
          q: "Correct: 'Is you a teacher?' → '_____ you a teacher?'", 
          a: "Are",
          tip: "You → Are."
        },
        { 
          type: "mc", 
          q: "Fix: 'Does they live in Paris?'", 
          options: ["Do they live", "No change"], 
          a: "Do they live",
          successTip: "DO THEY! They → DO (not DOES)! ✅"
        },
        { 
          type: "order", 
          q: "Corrige: 'Are he a doctor?' (Ordena la forma correcta)", 
          words: ["Is", "he", "a", "doctor?"], 
          a: "Is he a doctor?",
          tip: "He → Is (not Are)."
        },
        { 
          type: "mc", 
          q: "Fix: 'Do she work here?'", 
          options: ["Does she work", "No change"], 
          a: "Does she work"
        },
        { 
          type: "input", 
          q: "Correct: 'Does you speak English?' → '_____ you speak?'", 
          a: "Do",
          tip: "You → Do (not Does)."
        },
        { 
          type: "mc", 
          q: "'Is they engineers?' is correct.", 
          options: ["False", "True"], 
          a: "False",
          tip: "Should be 'Are they engineers?'",
          successTip: "FALSE! They → ARE! Common error fixed! 🎯"
        }
      ]
    },

    {
      id: "u17-set8",
      title: "Set 8: Mixed Practice",
      context: `*Lunes - Práctica final*

Combina todo lo aprendido:
Questions with TO BE, DO/DOES, WH-words, and short answers.
¡Es hora de demostrar todo lo que sabes!

*Monday - Final practice*

Combine everything learned:
Questions with TO BE, DO/DOES, WH-words, and short answers.
Time to show everything you know!`,
      
      questions: [
        { 
          type: "mc", 
          q: "'_____ you a student?'", 
          options: ["Are", "Is", "Do"], 
          a: "Are",
          successTip: "ARE! Identity question! ✅"
        },
        { 
          type: "mc", 
          q: "'_____ she work in a hospital?'", 
          options: ["Does", "Do", "Is"], 
          a: "Does",
          successTip: "DOES! Action question! ✅"
        },
        { 
          type: "input", 
          q: "'_____ is your name?' (qué)", 
          a: "What",
          successTip: "WHAT! Information question! 💭"
        },
        { 
          type: "mc", 
          q: "'Do you live in Paris?' Answer: 'Yes, _____.'", 
          options: ["I do", "I am", "I live"], 
          a: "I do",
          successTip: "I DO! Short answer! ✅"
        },
        { 
          type: "order", 
          q: "Traduce: '¿Dónde trabaja ella?'", 
          words: ["Where", "does", "she", "work?"], 
          a: "Where does she work?",
          successTip: "PERFECT! WH + does + subject + verb! 🎯"
        },
        { 
          type: "mc", 
          q: "'He _____ a teacher.'", 
          options: ["is", "are", "am"], 
          a: "is"
        },
        { 
          type: "input", 
          q: "'_____ they go swimming?' (auxiliar)", 
          a: "Do"
        },
        { 
          type: "mc", 
          q: "'Is she an actor?' Answer: 'No, _____.'", 
          options: ["she isn't", "she doesn't", "she not"], 
          a: "she isn't"
        },
        { 
          type: "mc", 
          q: "'Does he play tennis?' Answer: 'Yes, _____.'", 
          options: ["he does", "he is", "he plays"], 
          a: "he does"
        },
        { 
          type: "order", 
          q: "Traduce: '¿Qué hora es?'", 
          words: ["What", "time", "is", "it?"], 
          a: "What time is it?",
          successTip: "🏆 PERFECT! You mastered all question types! Reading next! 📖"
        }
      ]
    }
  ],

  // ═══════════════════════════════════════════════════════════════════════
  // 📖 READING SET: Maria's Job Interview (DIVIDIDO EN PASSAGES)
  // ═══════════════════════════════════════════════════════════════════════
  readingSet: {
    id: "u17-reading",
    title: "Reading: Maria's Job Interview",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**Meeting Maria**

Hi! My name is Maria Kowalski. I am from Poland, but now I live in London. I am 28 years old and I am a receptionist. I work in a big office building in the city center.`
      },
      {
        type: "mc",
        q: "Where is Maria from?",
        options: ["Poland", "London", "France"],
        a: "Poland",
        tip: "Read the first line carefully.",
        successTip: "✓ POLAND! But NOW she lives in London! 🇵🇱→🇬🇧"
      },
      {
        type: "mc",
        q: "Where does Maria live now?",
        options: ["London", "Poland", "Paris"],
        a: "London",
        tip: "'But now I live in...'",
        successTip: "✓ LONDON! 'But now' = present location! 🇬🇧"
      },
      {
        type: "mc",
        q: "What is Maria's job?",
        options: ["receptionist", "teacher", "nurse"],
        a: "receptionist",
        successTip: "✓ RECEPTIONIST! Office job! 💼"
      },

      {
        type: "passage",
        passage: `**Maria's Work Day**

I start work at 9am every day. First, I answer phone calls and emails. Then, I help visitors and give them information. I work five days a week, from Monday to Friday. I finish work at 5pm.`
      },
      {
        type: "mc",
        q: "What time does Maria start work?",
        options: ["9am", "8am", "10am"],
        a: "9am",
        tip: "'I start work at...'",
        successTip: "✓ 9AM! Standard office hours! 🕘"
      },
      {
        type: "mc",
        q: "What does Maria do first at work?",
        options: ["answer calls and emails", "have lunch", "go shopping"],
        a: "answer calls and emails",
        tip: "'First' shows what she does first.",
        successTip: "✓ CALLS & EMAILS! 'First' = primero! 📞📧"
      },
      {
        type: "mc",
        q: "How many days does Maria work?",
        options: ["5 days", "6 days", "7 days"],
        a: "5 days",
        tip: "Monday to Friday = 5 days.",
        successTip: "✓ 5 DAYS! Monday-Friday = typical work week! 📅"
      },

      {
        type: "passage",
        passage: `**Maria's Opinion About Her Job**

I like my job very much. The people in my office are very friendly. My boss is nice and the building is modern. There is a café downstairs where I have lunch.

On the weekend, I don't work. I relax at home, visit friends, or go shopping. Sometimes I go to the park near my house.`
      },
      {
        type: "mc",
        q: "Does Maria like her job?",
        options: ["Yes", "No", "Not mentioned"],
        a: "Yes",
        tip: "Look for 'I like my job'.",
        successTip: "✓ YES! 'Very much' = a lot! Happy worker! 😊"
      },
      {
        type: "mc",
        q: "Where does Maria have lunch?",
        options: ["in a café", "at home", "in the park"],
        a: "in a café",
        tip: "'There is a café downstairs...'",
        successTip: "✓ CAFÉ! Downstairs = below her office! ☕"
      },
      {
        type: "mc",
        q: "Does Maria work on Saturday?",
        options: ["No", "Yes", "Sometimes"],
        a: "No",
        tip: "Read the last paragraph.",
        successTip: "✓ NO! Weekends = free time! Saturday & Sunday off! 🎉"
      },
      {
        type: "mc",
        q: "The office building is _____.",
        options: ["modern", "old", "small"],
        a: "modern",
        tip: "'The building is modern.'",
        successTip: "✓ MODERN! New and up-to-date building! 🏢✨"
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ⭐ REVIEW SET
  // ═══════════════════════════════════════════════════════════════════════
  reviewSet: {
    id: "u17-review",
    title: "Unit 1 Review",
    category: "review",
    questions: [
      { 
        type: "mc", 
        q: "Review: '_____ you a teacher?'", 
        options: ["Are", "Is", "Do"], 
        a: "Are",
        tip: "You → Are.",
        successTip: "✓ ARE! Identity question! You remembered! ✅"
      },
      { 
        type: "mc", 
        q: "Review: '_____ she work in a hospital?'", 
        options: ["Does", "Do", "Is"], 
        a: "Does",
        tip: "Action with 'She' → Does.",
        successTip: "✓ DOES! Action question! Perfect! ✅"
      },
      { 
        type: "input", 
        q: "Review: '_____ is your name?' (question word)", 
        a: "What",
        tip: "Asking for name."
      },
      { 
        type: "mc", 
        q: "Review: 'Is he a doctor?' Answer: 'Yes, _____.'", 
        options: ["he is", "he does", "he do"], 
        a: "he is",
        tip: "Question with 'Is', answer with 'is'."
      },
      { 
        type: "mc", 
        q: "Review: 'Do you work here?' Answer: 'No, _____.'", 
        options: ["I don't", "I'm not", "I doesn't"], 
        a: "I don't",
        tip: "Question with 'Do', answer with 'don't'."
      },
      { 
        type: "order", 
        q: "Review: Traduce: '¿Dónde vive ella?'", 
        words: ["Where", "does", "she", "live?"], 
        a: "Where does she live?",
        tip: "WH + does + subject + verb."
      },
      { 
        type: "mc", 
        q: "Review: 'He _____ up at 7am every day.'", 
        options: ["gets", "get", "getting"], 
        a: "gets",
        tip: "He → verb+S."
      },
      { 
        type: "input", 
        q: "Review: 'They _____ engineers.' (to be)", 
        a: "are",
        tip: "They → are."
      },
      { 
        type: "mc", 
        q: "Review: Fix error: 'Do he work here?'", 
        options: ["Does he work", "No change"], 
        a: "Does he work",
        tip: "He → Does."
      },
      { 
        type: "mc", 
        q: "Review: 'Does she _____ swimming on Fridays?'", 
        options: ["go", "goes", "going"], 
        a: "go",
        tip: "After 'Does', base form.",
        successTip: "🏆 UNIT 1 COMPLETE! You mastered questions in Present Simple! Ready for Unit 2! 🎉"
      }
    ]
  }
};
